import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  RIDE_MODE,
  effectiveRiderContext,
  rideModeLabel,
  rideModeReducer,
} from "@/lib/rideMode";
import { loadGuestPreferences, patchGuestPreferences } from "@/lib/guestPreferences";

const RideModeContext = createContext(null);
const RIDE_MODE_COOKIE = "bpf_ride_mode";

export function RideModeProvider({ children }) {
  const { user } = useAuth();
  const [rideMode, dispatch] = useReducer(rideModeReducer, RIDE_MODE.USUAL);

  useEffect(() => {
    if (user === false) {
      dispatch({ type: "SET", value: loadGuestPreferences().rideMode });
    } else if (user) {
      dispatch({ type: "CLEAR" });
    }
  }, [user]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.cookie = `${RIDE_MODE_COOKIE}=${encodeURIComponent(rideMode)}; Path=/; SameSite=Lax`;
  }, [rideMode]);

  const setRideMode = useCallback((value) => {
    dispatch({ type: "SET", value });
    if (user === false) patchGuestPreferences({ rideMode: value });
  }, [user]);

  const clearRideMode = useCallback(() => {
    dispatch({ type: "CLEAR" });
    if (user === false) patchGuestPreferences({ rideMode: RIDE_MODE.USUAL });
  }, [user]);

  const value = useMemo(() => ({
    rideMode,
    rideModeLabel: rideModeLabel(rideMode),
    setRideMode,
    clearRideMode,
    effectiveContext: (profile) => effectiveRiderContext(profile, rideMode),
  }), [clearRideMode, rideMode, setRideMode]);

  return <RideModeContext.Provider value={value}>{children}</RideModeContext.Provider>;
}

export const useRideMode = () => useContext(RideModeContext);
